import React, { createContext, useContext, useState } from 'react';
import { DocumentData, OrderByDirection, QueryDocumentSnapshot } from 'firebase/firestore';
import { message } from 'antd';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../auth/AuthenticationContext';
import getUserById from '../../../../domain/use_cases/users/getUserById';
import getCompanies from '../../../../domain/use_cases/companies/getCompanies';
import getCompaniesWithFilters, {
  CompanyFilters,
} from '../../../../domain/use_cases/companies/getCompaniesWithFilters';
import getCompanyById from '../../../../domain/use_cases/companies/getCompanyById';
import deleteCompany from '../../../../domain/use_cases/companies/deleteCompany';
import UserDoc from '../../../../domain/models/UserDoc';
import getUsersByCompanyId from '../../../../domain/use_cases/companies/getUsersByCompanyId';
import Subscription from '../../../../domain/models/Subscription';
import { getCompanySubscriptions } from '../../../../domain/use_cases/companies/getCompanySubscriptions';
import removeUserFromCompany, {
  RemoveUserFromCompanyStatusFailure,
} from '../../../../domain/use_cases/companies/removeUserFromCompany';
import searchCompanies from '../../../../domain/use_cases/companies/searchCompanies';
import { ICompany } from '@leedpulse-monorepo/interfaces';

/** Options for fetch/fetchNext, aligned with PlansContext pattern */
export interface CompanyOptions {
  filters?: CompanyFilters;
  orderBy?: string;
  sort?: OrderByDirection;
}

/** Fetch signature: (options?, pagination?) or legacy (favoritesOnly, limit) */
export type FetchCompaniesFn = (
  optionsOrFavorites?: CompanyOptions | boolean,
  paginationOrLimit?: boolean | number
) => Promise<void>;

const CompaniesContext = createContext({
  count: 0,
  activeCount: 0,
  expiredCount: 0,
  items: [] as ICompany[],
  selected: null as ICompany | null,
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  searchResults: [] as ICompany[],
  searchValue: null as string | null,
  noSearchResults: false,
  favoritesOnly: false,
  companyUsers: [] as UserDoc[],
  companySubscriptions: [] as Subscription[],
  fetchUsers: (_: string) => new Promise((resolve, reject) => null),
  fetchSubscriptions: (_: string) => new Promise((resolve, reject) => null),
  removeUser: (email: string) => new Promise((resolve, reject) => null),
  fetch: (async (_?: CompanyOptions | boolean, __?: boolean | number) => {
    return;
  }) as FetchCompaniesFn,
  fetchNext: (_options?: CompanyOptions) => new Promise((resolve, reject) => null),
  fetchAndSelectById: (_: string) => new Promise((resolve, reject) => null),
  fetchWithFilters: (
    _f: CompanyFilters,
    _o?: string,
    _s?: OrderByDirection,
    _l?: number
  ) => new Promise((resolve, reject) => null),
  fetchNextWithFilters: (
    _f: CompanyFilters,
    _o?: string,
    _s?: OrderByDirection,
    _l?: number
  ) => new Promise((resolve, reject) => null),
  search: (_: string, _filters?: CompanyFilters) => new Promise((resolve, reject) => null),
  resetSearch: () => { return; },
  reset: () => { return; },
  select: (_: ICompany) => { return; },
  removeCompany: (_: string) => { return; },
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
  statusCount: ContextStatus.IDLE,
  statusAddRemoveUser: ContextStatus.IDLE,
  errorMessage: '',
});

export const COMPANIES_FETCH_LIMIT = 10;

export const CompaniesProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const [items, setItems] = useState<ICompany[]>([]);
  const [selected, setSelected] = useState<ICompany | null>(null);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [companyUsers, setCompanyUsers] = useState<UserDoc[]>([]);
  const [companySubscriptions, setCompanySubscriptions] = useState<Subscription[]>([]);
  const [searchResults, setSearchResults] = useState<ICompany[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);
  const [statusCount, setStatusCount] = useState(ContextStatus.IDLE);
  const [statusAddRemoveUser, setStatusAddRemoveUser] = useState(
    ContextStatus.IDLE
  );
  const [errorMessage, setErrorMessage] = useState('');

  const authContext = useContext(AuthenticationContext);

  /*****************************/
  /**          Users          **/ 
  /*****************************/
  const fetchUsers = async (companyId: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getUsersByCompanyId(companyId);
      setCompanyUsers(useCaseResponse);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const removeUser = async (email: string) => {
    setStatusAddRemoveUser(ContextStatus.LOADING);
    try {
      if (!selected || !authContext.authentication) {
        setStatusAddRemoveUser(ContextStatus.ERROR);
        return;
      }

      const user = companyUsers.find(
        (u) => u.emailAuthentication === email
      );

      if (!user) {
        throw new RemoveUserFromCompanyStatusFailure('Utilisateur non trouvé');
      }

      await removeUserFromCompany(selected, authContext.authentication, user.uid);
      setStatusAddRemoveUser(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof RemoveUserFromCompanyStatusFailure) {
        setErrorMessage(e.message);
      }
      setStatusAddRemoveUser(ContextStatus.ERROR);
    }
  };

  /** Fetch companies. Supports PlansContext-style (options, pagination) or legacy (favoritesOnly, limit) */
  const fetch = async (
    optionsOrFavorites?: CompanyOptions | boolean,
    paginationOrLimit: boolean | number = false
  ) => {
    setStatus(ContextStatus.LOADING);
    try {
      const isLegacyCall = typeof optionsOrFavorites === 'boolean';
      const fetchFavoritesOnly = isLegacyCall
        ? (optionsOrFavorites as boolean)
        : false;
      const limit =
        typeof paginationOrLimit === 'number'
          ? paginationOrLimit
          : paginationOrLimit
            ? COMPANIES_FETCH_LIMIT
            : 100;

      if (fetchFavoritesOnly && authContext.authentication?.id) {
        setFavoritesOnly(true);
        const userResponse = await getUserById(
          authContext.authentication.id
        );
        const useCaseResponse = await getCompanies(
          1000,
          undefined,
          Object.keys(userResponse?.idCompanies || {})
        );
        setItems(useCaseResponse.companies);
        setLastVisible(useCaseResponse.lastVisible);
        setCount(useCaseResponse.totalCollectionCount);
      } else if (!isLegacyCall && (optionsOrFavorites as CompanyOptions)?.filters) {
        const opts = optionsOrFavorites as CompanyOptions;
        const useCaseResponse = await getCompaniesWithFilters(
          opts.filters ?? {},
          opts.orderBy,
          opts.sort,
          limit
        );
        setItems(useCaseResponse.companies);
        setLastVisible(useCaseResponse.lastVisible);
        setCount(useCaseResponse.totalCount);
      } else {
        const useCaseResponse = await getCompanies(limit);
        setItems(useCaseResponse.companies);
        setLastVisible(useCaseResponse.lastVisible);
        setCount(useCaseResponse.totalCollectionCount);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  /*****************************/
  /** Subscriptions (numbers) **/ 
  /*****************************/
  const fetchSubscriptions = async (companyId: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      if (!authContext.authentication) throw new Error('Authentication is required');

      const useCaseResponse = await getCompanySubscriptions({ company_id: companyId }, authContext.authentication);
      setCompanySubscriptions(useCaseResponse);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  /*****************************/

  const fetchNext = async (options?: CompanyOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      if (options?.filters) {
        const useCaseResponse = await getCompaniesWithFilters(
          options.filters,
          options.orderBy,
          options.sort,
          COMPANIES_FETCH_LIMIT,
          lastVisible
        );
        setItems((prev) => [...prev, ...useCaseResponse.companies]);
        setLastVisible(useCaseResponse.lastVisible);
      } else {
        const useCaseResponse = await getCompanies(
          COMPANIES_FETCH_LIMIT,
          lastVisible
        );
        setItems((prev) => [...prev, ...useCaseResponse.companies]);
        setLastVisible(useCaseResponse.lastVisible);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAndSelectById = async (id: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const company = await getCompanyById(id, authContext.authentication);
      if (company) {
        setSelected(company);
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const search = async (
    searchQuery: string,
    filters?: CompanyFilters
  ) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await searchCompanies(authContext.authentication, searchQuery, 0, 20, {});
      const now = Date.now();
      const results: ICompany[] = useCaseResponse.hits.map((hit) =>
      ({
        id: String(hit.objectID ?? hit.id ?? ''),
        id_stripe: String(hit.id_stripe ?? ''),
        api_key: String(hit.api_key ?? ''),
        id_subscriptions: (hit.id_subscriptions as Record<string, { updated: number }>) ?? {},
        name: String(hit.name ?? ''),
        created: hit.created ? hit.created : now,
        updated: hit.updated ? hit.updated : now,
      }));
      setSearchResults(results);
      setNoSearchResults(results.length === 0);
      setSearchValue(searchQuery);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const resetSearch = () => {
    setSearchResults([]);
    setNoSearchResults(false);
    setSearchValue(null);
  };

  const reset = () => {
    setItems([]);
    setCount(0);
    setSelected(null);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setSearchResults([]);
    setNoSearchResults(false);
    setSearchValue(null);
    setStatus(ContextStatus.IDLE);
    setStatusCount(ContextStatus.IDLE);
    setStatusAddRemoveUser(ContextStatus.IDLE);
    setErrorMessage('');
  };

  const select = (company: ICompany) => {
    setSelected(company);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  const removeCompany = async (companyId: string) => {
    if (!authContext.authentication?.id) return;
    setStatus(ContextStatus.LOADING);
    try {
      await deleteCompany(companyId, authContext.authentication);
      setItems((prev) => prev.filter((c) => c.id !== companyId));
      setStatus(ContextStatus.SUCCESS);
      message.success('Compagnie supprimée avec succès');
    } catch (e) {
      setStatus(ContextStatus.ERROR);
      message.error('Une erreur est survenue lors de la suppression de la compagnie');
    }
  };

  /** Alias for CompaniesPage: fetch with filters, order, sort */
  const fetchWithFilters = async (
    filters: CompanyFilters,
    orderBy: string | undefined,
    sort: OrderByDirection | undefined,
    limit = COMPANIES_FETCH_LIMIT
  ) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getCompaniesWithFilters(
        filters,
        orderBy,
        sort,
        limit
      );
      setItems(useCaseResponse.companies);
      setCount(useCaseResponse.totalCount);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  /** Alias for CompaniesList: fetch next page with filters */
  const fetchNextWithFilters = async (
    filters: CompanyFilters,
    orderBy: string | undefined,
    sort: OrderByDirection | undefined,
    limit = COMPANIES_FETCH_LIMIT
  ) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getCompaniesWithFilters(
        filters,
        orderBy,
        sort,
        limit,
        lastVisible
      );
      setItems((prev) => [...prev, ...useCaseResponse.companies]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <CompaniesContext.Provider
      value={{
        count,
        activeCount,
        expiredCount,
        items,
        selected,
        lastVisible,
        searchResults,
        searchValue,
        noSearchResults,
        favoritesOnly,
        companyUsers,
        companySubscriptions,
        fetchUsers,
        fetchSubscriptions,
        removeUser,
        fetch,
        fetchNext,
        fetchAndSelectById,
        search,
        resetSearch,
        reset,
        select,
        removeCompany,
        fetchWithFilters,
        fetchNextWithFilters,
        status,
        selectionStatus,
        statusCount,
        statusAddRemoveUser,
        errorMessage,
      }}
    >
      {children}
    </CompaniesContext.Provider>
  );
};

export default CompaniesContext;
